import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import _ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import ViewsIcon from '@mui/icons-material/DynamicFeed';
import PoliciesIcon from '@mui/icons-material/Policy';
import SensorsIcon from '@mui/icons-material/Assessment';
import SystemIcon from '@mui/icons-material/Settings';

import AlertsIcon from '@mui/icons-material/Notifications';
import MetadataIcon from '@mui/icons-material/Apps';
import PacketCaptureIcon from '@mui/icons-material/SettingsEthernet';
import UsersIcon from '@mui/icons-material/Person';
import ConfigurationIcon from '@mui/icons-material/SettingsApplications';
import CMCHealthIcon from '@mui/icons-material/Healing';
import AuditIcon from '@mui/icons-material/Subject';
import ReportsIcon from '@mui/icons-material/Assignment';
import UpgradeIcon from '@mui/icons-material/Update';

import Auth from '../../data/auth-service';
import DataContext from '../../data/data-context';
import Permissions from '../../data/permissions';

const drawerWidth = 300;
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        color: theme.palette.text.primary,

        '& .MuiDrawer-paperAnchorDockedLeft': {
            borderStyle: 'none',
        },
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        // scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        transition: 'width 500ms',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        scrollbarWidth: 'none'
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        minHeight: 85,
        ...theme.mixins.toolbar,
    },
    list: {
        flex: 1,
        width: '100%',
        minWidth: 275,
        maxWidth: 300,
        marginTop: 12,
        backgroundColor: theme.palette.nav.paperAlt,
        color: theme.palette.common.white,
        '& .MuiListItem-button': {
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
            paddingTop: '10px',
            paddingBottom: '10px',
            minHeight: 53,
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none'
    },
    listSubheader: {
        color: theme.palette.common.white,
    },
    nested: {
        paddingLeft: theme.spacing(4),
        backgroundColor: theme.palette.nav.nested,

        '&:hover': {
            // text-decoration: none;
            backgroundColor: `${alpha(theme.palette.common.white, 0.08)}`,
        },
    },
    closeButton: {
        color: theme.palette.common.white,
        float: 'right',
        padding: '6px 0',
        minWidth: '24px',
    },
    selected: {
        background: theme.palette.nav.selection,
        borderRight: `9px solid ${theme.palette.nav.primary}`,
    },
    customIcon: {
        width: 22,
        height: 22,
    },
    secondaryColor: {
        color: theme.palette.secondary.main,
    },
    sensorErrors: {
        background: theme.palette.error.main,
        padding: 2,
        borderRadius: 30,
        width: 30,
        color: theme.palette.error.contrastText,
        textAlign: 'center',
        fontSize: 17,
    },
    sidebarBottomSpacer: {
        height: '48px',
    },
}));

// show/hide ListItem based on permissions
const ListItem = ({ noCheck, perm, children, ...rest }) => {
    const p = !perm ? {} : Object.prototype.toString.call(perm).includes('Object') ? perm : {};
    return (noCheck || Auth.perm(p))
        ? <_ListItem {...rest}> { children } </_ListItem>
        : null;
};
ListItem.propTypes = {
    perm: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    children: PropTypes.node,
    noCheck: PropTypes.bool,
};

const SideNav = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { systemHealth, sidenav: _sidenav, setSidenav: _setSidenav } = useContext(DataContext);
    // keep a copy in a local state for faster response
    const [sidenav, setSidenav] = useState(DataContext);

    const handleLink = link => {
        navigate(link);
    };

    const handleNavCollapse = state => {
        // once for us for fast feedback and once for the rest of the app.
        setSidenav(current => {
            if (!current) return { collapsed: !state };
            current.collapsed = !state;
            return current;
        });
        _setSidenav({ collapsed: !state });
    };

    const setPanels = useCallback(panels => {
        // once for us for fast feedback and once for the rest of the app.
        setSidenav(current => {
            if (!current) return { panels };
            current.panels = panels;
            return current;
        });
        _setSidenav({ panels });
    }, [_setSidenav]);

    const panels = useCallback(() => {
        return sidenav.collapsed ? {} : (sidenav.panels || {});
    }, [sidenav]);

    useEffect(() => {
        const locData = location.pathname.split('/').filter(v => Boolean(v));
        if (locData.length) {
            setPanels({
                [locData[0]]: true,
            });
        }
    }, [location]);

    useEffect(() => {
        setSidenav(_sidenav);
    }, [_sidenav]);

    return (
        <div className={classes.root}>
            <Drawer
                open={true}
                className={clsx(classes.drawer, { [classes.drawerCollapsed]: sidenav.collapsed })}
                variant="persistent"
                classes={{
                    paper: clsx(classes.drawerPaper, { [classes.drawerCollapsed]: sidenav.collapsed }),
                }}
            >
                <div className={classes.toolbar} />
                <List component="nav" className={classes.list} aria-label="Side Nav Bar" style={props.header ? { marginTop: '42px' } : {}}>
                    <ListItem noCheck button onClick={() => { handleNavCollapse(sidenav.collapsed) }} aria-label="Expand or Collpase Side Navr Bar">
                        <ListItemIcon className={classes.listSubheader}>
                            {sidenav.collapsed ? <NavigateNextIcon/> : <NavigateBeforeIcon/>}
                        </ListItemIcon>
                    </ListItem>
                    <ListItem
                        perm={{ ...Permissions.Views }}
                        button
                        onClick={() => { handleLink('views') }}
                        className={clsx({ [classes.selected]: (props.path.match('views')) })}
                        aria-label="Open Views page"
                    >
                        <ListItemIcon><ViewsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('views') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Views"></ListItemText>
                        {panels().views ? <ExpandLessIcon onClick={(e) => { e.stopPropagation(); setPanels({ views: false }) }} aria-label="Collapse Views sub-menu"/> : <ExpandMoreIcon onClick={(e) => { e.stopPropagation(); setPanels({ views: true }) }} aria-label="Expand Views sub-menu" />}
                    </ListItem>
                    <Collapse in={panels().views} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem perm={{ Alerts: [...Permissions.Views.Alerts] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/views/alerts') })} aria-label="Open Alerts page" onClick={() => { handleLink('/views/alerts') }}>
                                <ListItemIcon><AlertsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/views/alerts') })} /></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Alerts"></ListItemText>
                            </ListItem>
                            <ListItem perm={{ Metadata: [...Permissions.Views.Metadata] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/views/metadata') })} aria-label="Open Metadata page" onClick={() => { handleLink('/views/metadata') }}>
                                <ListItemIcon><MetadataIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/views/metadata') })} /></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Metadata"></ListItemText>
                            </ListItem>
                            <ListItem perm={{ Pcap: [...Permissions.Views.Pcap] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/views/packet_requests') })} aria-label="Open Packet Capture page" onClick={() => { handleLink('/views/packet_requests') }}>
                                <ListItemIcon><PacketCaptureIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/views/packet_requests') })} /></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Packet Requests"></ListItemText>
                            </ListItem>
                        </List>
                    </Collapse>
                    <ListItem
                        perm={{ ...Permissions.Policies.Sensor, ...Permissions.Policies.Suricata, ...Permissions.Policies.Zeek }}
                        button
                        onClick={() => { handleLink('/policies') }}
                        className={clsx({ [classes.selected]: (props.path.match('policies')) })}
                        aria-label="Open Policies page"
                    >
                        <ListItemIcon><PoliciesIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('policies') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Policies"></ListItemText>
                        {panels().policies ? <ExpandLessIcon onClick={(e) => { e.stopPropagation(); setPanels({ policies: false }) }} aria-label="Collapse Policies sub-menu"/> : <ExpandMoreIcon onClick={(e) => { e.stopPropagation(); setPanels({ policies: true }) }} aria-label="Expand Policies sub-menu" />}
                    </ListItem>
                    <Collapse in={panels().policies} timeout="auto" unmountOnExit>
                        <List componet="div" disablePadding>
                            <ListItem
                                perm={{ ...Permissions.Policies.Sensor }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/policies/sensor') })} aria-label="Open Sensor Policies page" onClick={() => { handleLink('/policies/sensor') }}>
                                <ListItemIcon>
                                    <img
                                        className={classes.customIcon}
                                        src={props.path.match('/policies/sensor') ? '/images/icon-new-secondary.png' : '/images/icon-new.png'}
                                    />
                                </ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="OpenText™ NDR" />
                            </ListItem>
                            <ListItem
                                perm={{ ...Permissions.Policies.Suricata }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/policies/suricata') })} aria-label="Open Suricata Policies page" onClick={() => { handleLink('/policies/suricata') }}>
                                <ListItemIcon>
                                    <img
                                        className={classes.customIcon}
                                        src={props.path.match('/policies/suricata') ? '/images/icon-suricata-secondary.png' : '/images/icon-suricata.png'}
                                    />
                                </ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Suricata" />
                            </ListItem>
                            <ListItem
                                perm={{ ...Permissions.Policies.Zeek }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/policies/zeek') })} aria-label="Open Zeek Policies page" onClick={() => { handleLink('/policies/zeek') }}>
                                <ListItemIcon>
                                    <img
                                        className={classes.customIcon}
                                        src={props.path.match('/policies/zeek') ? '/images/icon-zeek-secondary.png' : '/images/icon-zeek.png'}
                                    />
                                </ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Zeek" />
                            </ListItem>
                        </List>
                    </Collapse>
                    <ListItem
                        perm={{ CMC: [...Permissions.CMC] }}
                        button
                        onClick={() => { handleLink('/cmcs') }}
                        className={clsx({ [classes.selected]: (props.path.match('cmcs')) })}
                        aria-label="Open CMCs page"
                    >
                        <ListItemIcon><SensorsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('cmcs') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="CMCs"></ListItemText>
                    </ListItem>
                    <ListItem
                        perm={{ ...Permissions.System }}
                        button
                        onClick={() => { handleLink('/system') }}
                        className={clsx({ [classes.selected]: (props.path.match('system')) })}
                        aria-label="Open System page"
                    >
                        <ListItemIcon><SystemIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('system') })}/></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="System" />
                        <Box display={systemHealth && systemHealth.status === 'critical' ? 'flex' : 'none'} mr={1}>
                            <Box className={classes.sensorErrors}>!</Box>
                        </Box>
                        {panels().system ? <ExpandLessIcon onClick={(e) => { e.stopPropagation(); setPanels({ system: false }) }} aria-label="Collapse System sub-menu" /> : <ExpandMoreIcon onClick={(e) => { e.stopPropagation(); setPanels({ system: true }) }} aria-label="Expand System sub-menu" />}
                    </ListItem>
                    <Collapse in={panels().system} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem
                                perm={{
                                    Users: [...Permissions.System.Users],
                                    Roles: [...Permissions.System.Roles],
                                }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/system/users_roles') })} aria-label="Open Users & Roles page" onClick={() => { handleLink('/system/users_roles') }}>
                                <ListItemIcon><UsersIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/system/users_roles') })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Users & Roles" />
                            </ListItem>
                            <ListItem
                                perm={{ ...Permissions.System.Configuration }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/system/configuration') })} aria-label="Open Configuration page" onClick={() => { handleLink('/system/configuration') }}>
                                <ListItemIcon><ConfigurationIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/system/configuration') })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Configuration" />
                            </ListItem>
                            <ListItem
                                perm={{ SystemHealth: [...Permissions.System.SystemHealth] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/system/mc2_health') })} aria-label="Open MC2 Health page" onClick={() => { handleLink('/system/mc2_health') }}>
                                <ListItemIcon><CMCHealthIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/system/mc2_health') })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="MC2 Health" />
                                <Box display={systemHealth && systemHealth.status === 'critical' ? 'flex' : 'none'}>
                                    <ListItemSecondaryAction>
                                        <Box className={classes.sensorErrors}>!</Box>
                                    </ListItemSecondaryAction>
                                </Box>
                            </ListItem>
                            <ListItem
                                perm={{ Mc2Upgrades: [...Permissions.System.Upgrades.Mc2Upgrades] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match('/system/upgrades_mc2') })} aria-label="Open MC2 Upgrade page" onClick={() => { handleLink('/system/upgrades_mc2') }}>
                                <ListItemIcon><UpgradeIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('/system/upgrades_mc2') })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="MC2 Upgrades" />
                            </ListItem>
                            <ListItem
                                perm={{ Audit: [...Permissions.System.Audit] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match(/\/system\/audit$/) })} aria-label="Open Audit page" onClick={() => { handleLink('/system/audit') }}>
                                <ListItemIcon><AuditIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match(/\/system\/audit$/) })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Audit" />
                            </ListItem>
                            <ListItem
                                perm={{ Audit: [...Permissions.System.Audit] }}
                                button className={clsx(classes.nested, { [classes.selected]: props.path.match(/\/system\/audit_reports$/) })} aria-label="Open Audit report page" onClick={() => { handleLink('/system/audit_reports') }}>
                                <ListItemIcon><ReportsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match(/\/system\/audit_reports$/) })}/></ListItemIcon>
                                <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Audit Reports" />
                            </ListItem>
                            <ListItem className={classes.sidebarBottomSpacer}></ListItem>
                        </List>
                    </Collapse>
                </List>
            </Drawer>
        </div>
    );
};
SideNav.propTypes = {
    path: PropTypes.string,
    header: PropTypes.bool,
};

export default SideNav;
